import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Typography, IconButton, Tooltip, Box, Collapse } from '@mui/material';
import {
    FaSignOutAlt,
    FaTachometerAlt,
    FaUsers,
    FaStore,
    FaClipboardList,
    FaTags,
    FaHandshake,
    FaBolt,
    FaFlag,
    FaWallet,
    FaFileInvoiceDollar,
    FaHeadset,
    FaChartBar,
    FaUserShield,
    FaServer,
    FaBullhorn,
    FaMobileAlt,
    FaMapMarkedAlt,
    FaCog,
    FaTicketAlt,
    FaTruck,
    FaChevronDown,
    FaChevronRight,
} from 'react-icons/fa';
import AppColors from '../../utils/AppColors';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import socketService from '../../services/socketService';

const patternImage = '/Pattern.png';

// One-time animation keyframes
if (typeof document !== 'undefined' && !document.querySelector('#sidebar-animations')) {
    const style = document.createElement('style');
    style.id = 'sidebar-animations';
    style.textContent = `
        @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.12); }
            100% { transform: scale(1); }
        }
        .jaddid-sidebar-scroll::-webkit-scrollbar { width: 6px; }
        .jaddid-sidebar-scroll::-webkit-scrollbar-thumb {
            background: rgba(14,37,55,0.18); border-radius: 6px;
        }
        .jaddid-sidebar-scroll::-webkit-scrollbar-thumb:hover {
            background: rgba(14,37,55,0.32);
        }
    `;
    document.head.appendChild(style);
}

// ---- Navigation model ---------------------------------------------------
// Items are grouped into sections. Each item declares an `exact` flag when
// its path is a prefix of another item, so active-state matching is precise.
// Vouchers business is paused; suppliers/vouchers are hidden via flag.

const NAV_SECTIONS = [
    {
        id: 'overview',
        label: 'Overview',
        items: [
            { path: '/dashboard', label: 'Dashboard', icon: <FaTachometerAlt />, exact: true },
            { path: '/analytics', label: 'Analytics', icon: <FaChartBar /> },
        ],
    },
    {
        id: 'marketplace',
        label: 'Marketplace',
        items: [
            { path: '/listings', label: 'Listings', icon: <FaTags />, exact: true },
            { path: '/listings/approvals', label: 'Listing Approvals', icon: <FaClipboardList /> },
            { path: '/c2c-offers', label: 'C2C Offers', icon: <FaHandshake /> },
            { path: '/tango-quotes', label: 'TangoPlus Quotes', icon: <FaBolt /> },
            { path: '/listing-reports', label: 'Listing Reports', icon: <FaFlag /> },
            { path: '/requests', label: 'Selling Requests', icon: <FaTruck /> },
        ],
    },
    {
        id: 'people',
        label: 'People',
        items: [
            { path: '/users', label: 'Users', icon: <FaUsers /> },
            { path: '/vendors', label: 'Vendors', icon: <FaStore /> },
            { path: '/voucher-suppliers', label: 'Voucher Suppliers', icon: <FaTicketAlt />, hidden: true },
            { path: '/vouchers', label: 'All Vouchers', icon: <FaTicketAlt />, hidden: true },
        ],
    },
    {
        id: 'finance',
        label: 'Finance',
        items: [
            { path: '/transactions', label: 'Transactions', icon: <FaWallet /> },
            { path: '/accounting-reports', label: 'Accounting Reports', icon: <FaFileInvoiceDollar /> },
        ],
    },
    {
        id: 'support',
        label: 'Support & Comms',
        items: [
            { path: '/support', label: 'Support', icon: <FaHeadset />, badgeKey: 'support' },
            { path: '/announcements', label: 'Announcements', icon: <FaBullhorn /> },
            { path: '/promo-codes', label: 'Promo Codes', icon: <FaTicketAlt /> },
        ],
    },
    {
        id: 'admin',
        label: 'Administration',
        items: [
            { path: '/admin-management', label: 'Admin Management', icon: <FaUserShield />, requiresSuperAdmin: true },
            { path: '/system', label: 'System', icon: <FaServer /> },
            { path: '/app-versions', label: 'App Versions', icon: <FaMobileAlt /> },
            { path: '/emirates', label: 'Emirates Coverage', icon: <FaMapMarkedAlt /> },
            { path: '/catalog', label: 'Listing Catalog', icon: <FaTags /> },
            { path: '/settings', label: 'Settings', icon: <FaCog /> },
        ],
    },
];

const COLLAPSED_OPEN_KEY = 'jaddid_admin_sidebar_open_groups';

const Sidebar = ({ isOpen }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();
    const [totalUnreadCount, setTotalUnreadCount] = useState(0);

    const [openGroups, setOpenGroups] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(COLLAPSED_OPEN_KEY) || '{}');
            return { ...defaultOpenGroups(), ...saved };
        } catch {
            return defaultOpenGroups();
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem(COLLAPSED_OPEN_KEY, JSON.stringify(openGroups));
        } catch { /* ignore quota errors */ }
    }, [openGroups]);

    const toggleGroup = (id) => setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));

    // ---- Auth-aware nav model ---------------------------------------------
    const sections = useMemo(() => {
        return NAV_SECTIONS.map((section) => ({
            ...section,
            items: section.items.filter((item) => {
                if (item.hidden) return false;
                if (item.requiresSuperAdmin && user?.role !== 'super_admin') return false;
                return true;
            }),
        })).filter((s) => s.items.length > 0);
    }, [user]);

    // ---- Active-route detection (FIX) -------------------------------------
    // Bug previously: path.startsWith(item.path) caused both `/listings` and
    // `/listings/approvals` to be active simultaneously. We now require an
    // exact match (or `/` boundary) and treat items flagged `exact` strictly.
    const isActiveRoute = (item) => {
        const current = location.pathname;
        if (current === item.path) return true;
        if (item.exact) return false;
        return current.startsWith(item.path + '/');
    };

    // ---- Unread support count --------------------------------------------
    const fetchTotalUnreadCount = async () => {
        try {
            const response = await api.get('/admin/support/unread-count');
            setTotalUnreadCount(response.data.count || 0);
        } catch (error) {
            console.error('Error fetching unread count for sidebar:', error);
            setTotalUnreadCount(0);
        }
    };

    useEffect(() => {
        let messageHandler = null;
        let chatHandler = null;

        const init = async () => {
            try {
                await fetchTotalUnreadCount();
                const token = localStorage.getItem('adminToken');
                if (token) {
                    try { await socketService.connect(token); } catch { /* already connected */ }
                }
                messageHandler = (data) => {
                    const isUser = data?.isUser === true || data?.is_user === true;
                    const senderType = (data?.senderType || data?.sender_type || '').toLowerCase();
                    if (isUser || senderType === 'user' || senderType === 'vendor') {
                        setTotalUnreadCount((prev) => prev + 1);
                    }
                };
                chatHandler = messageHandler;
                if (socketService.socket) {
                    socketService.socket.on('support_message_received', messageHandler);
                    socketService.socket.on('chat_message', chatHandler);
                }
                socketService.onUnreadCountUpdated(() => fetchTotalUnreadCount());
            } catch (error) {
                console.error('Error initializing sidebar data:', error);
            }
        };
        init();

        const handleUnreadUpdated = () => fetchTotalUnreadCount();
        window.addEventListener('support-unread-updated', handleUnreadUpdated);
        const pollInterval = setInterval(fetchTotalUnreadCount, 30000);

        return () => {
            if (socketService.socket) {
                if (messageHandler) socketService.socket.off('support_message_received', messageHandler);
                if (chatHandler) socketService.socket.off('chat_message', chatHandler);
            }
            socketService.offUnreadCountUpdated();
            window.removeEventListener('support-unread-updated', handleUnreadUpdated);
            clearInterval(pollInterval);
        };
    }, []); // eslint-disable-line

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const badgeFor = (item) => (item.badgeKey === 'support' ? totalUnreadCount : 0);

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                height: '100vh',
                width: isOpen ? '240px' : '74px',
                background: 'rgba(255,255,255,0.92)',
                backdropFilter: 'blur(14px)',
                borderRight: '1px solid rgba(14,37,55,0.06)',
                boxShadow: '0 8px 32px rgba(14,37,55,0.06)',
                display: 'flex',
                flexDirection: 'column',
                transition: 'width 0.25s ease',
                zIndex: 1000,
                overflow: 'hidden',
            }}
        >
            {/* Logo */}
            <Box sx={{ pt: 3, pb: 2, px: 2, textAlign: 'center', position: 'relative', flexShrink: 0 }}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: -50,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 260,
                        height: 200,
                        backgroundImage: `url(${patternImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        opacity: 0.12,
                        zIndex: 0,
                        pointerEvents: 'none',
                    }}
                />
                <img
                    src="/jaddid-logo.png"
                    alt="Jaddid Admin"
                    style={{
                        position: 'relative',
                        zIndex: 1,
                        width: isOpen ? 110 : 44,
                        transition: 'width 0.25s ease',
                    }}
                />
            </Box>

            {/* Nav (scrollable) */}
            <Box
                className="jaddid-sidebar-scroll"
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    px: isOpen ? 1.25 : 0.75,
                    py: 1,
                }}
            >
                {sections.map((section) => (
                    <Box key={section.id} sx={{ mb: 1 }}>
                        {isOpen ? (
                            <Box
                                onClick={() => toggleGroup(section.id)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    px: 1.25,
                                    py: 0.75,
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    color: AppColors.textSecondary,
                                    '&:hover': { color: AppColors.primaryDark },
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.8px',
                                        fontSize: '0.68rem',
                                    }}
                                >
                                    {section.label}
                                </Typography>
                                <Box sx={{ fontSize: '0.6rem', display: 'flex' }}>
                                    {openGroups[section.id] === false ? <FaChevronRight /> : <FaChevronDown />}
                                </Box>
                            </Box>
                        ) : (
                            <Box sx={{ height: 6 }} />
                        )}

                        <Collapse in={!isOpen || openGroups[section.id] !== false} timeout={150}>
                            {section.items.map((item) => {
                                const active = isActiveRoute(item);
                                const badge = badgeFor(item);
                                return isOpen ? (
                                    <NavRow
                                        key={item.path}
                                        item={item}
                                        active={active}
                                        badge={badge}
                                        onClick={() => navigate(item.path)}
                                    />
                                ) : (
                                    <CollapsedRow
                                        key={item.path}
                                        item={item}
                                        active={active}
                                        badge={badge}
                                        onClick={() => navigate(item.path)}
                                    />
                                );
                            })}
                        </Collapse>
                    </Box>
                ))}
            </Box>

            {/* User strip */}
            <Box
                sx={{
                    flexShrink: 0,
                    p: isOpen ? 1.5 : 1,
                    borderTop: '1px solid rgba(14,37,55,0.06)',
                    background: 'rgba(245,247,250,0.6)',
                }}
            >
                {isOpen ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 1, py: 0.5 }}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 700,
                                    color: AppColors.primaryDark,
                                    fontSize: '0.85rem',
                                    lineHeight: 1.2,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {user?.name || 'Admin'}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: AppColors.textSecondary,
                                    fontSize: '0.7rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'block',
                                }}
                            >
                                {user?.email || 'admin@jaddid.ae'}
                            </Typography>
                        </Box>
                        <Tooltip title="Logout">
                            <IconButton
                                onClick={handleLogout}
                                size="small"
                                sx={{ color: AppColors.primaryDark, '&:hover': { color: AppColors.errorRed, bgcolor: 'rgba(244,67,54,0.08)' } }}
                            >
                                <FaSignOutAlt />
                            </IconButton>
                        </Tooltip>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Tooltip title="Logout" placement="right">
                            <IconButton
                                onClick={handleLogout}
                                size="small"
                                sx={{ color: AppColors.primaryDark, '&:hover': { color: AppColors.errorRed, bgcolor: 'rgba(244,67,54,0.08)' } }}
                            >
                                <FaSignOutAlt />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

// ---- Subcomponents ------------------------------------------------------

const NavRow = ({ item, active, badge, onClick }) => (
    <Box sx={{ position: 'relative' }}>
        <Box
            component="button"
            onClick={onClick}
            sx={{
                width: '100%',
                textAlign: 'left',
                border: 'none',
                cursor: 'pointer',
                background: active
                    ? `linear-gradient(135deg, ${AppColors.primaryDark} 0%, #1d3a55 100%)`
                    : 'transparent',
                color: active ? '#fff' : AppColors.primaryDark,
                borderRadius: '12px',
                px: 1.25,
                py: 1,
                my: 0.25,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                fontSize: '0.875rem',
                fontWeight: active ? 600 : 500,
                fontFamily: 'inherit',
                transition: 'background 150ms ease, color 150ms ease',
                boxShadow: active ? '0 6px 16px rgba(14,37,55,0.18)' : 'none',
                '&:hover': {
                    background: active
                        ? `linear-gradient(135deg, ${AppColors.primaryDark} 0%, #1d3a55 100%)`
                        : 'rgba(14,37,55,0.06)',
                },
            }}
        >
            <Box sx={{ width: 22, display: 'flex', justifyContent: 'center', color: active ? '#fff' : AppColors.primaryDark, fontSize: '0.95rem' }}>
                {item.icon}
            </Box>
            <Box sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</Box>
        </Box>
        {badge > 0 && (
            <Box
                sx={{
                    position: 'absolute',
                    top: 8,
                    right: 10,
                    minWidth: 20,
                    height: 20,
                    px: 0.75,
                    borderRadius: '12px',
                    background: AppColors.errorRed,
                    color: '#fff',
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 6px rgba(244,67,54,0.4)',
                    animation: 'pulse 2s infinite',
                }}
            >
                {badge > 99 ? '99+' : badge}
            </Box>
        )}
    </Box>
);

const CollapsedRow = ({ item, active, badge, onClick }) => (
    <Tooltip title={item.label} placement="right" arrow>
        <Box sx={{ position: 'relative' }}>
            <Box
                component="button"
                onClick={onClick}
                sx={{
                    width: '100%',
                    border: 'none',
                    cursor: 'pointer',
                    background: active
                        ? `linear-gradient(135deg, ${AppColors.primaryDark} 0%, #1d3a55 100%)`
                        : 'transparent',
                    color: active ? '#fff' : AppColors.primaryDark,
                    borderRadius: '12px',
                    py: 1.1,
                    my: 0.3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.05rem',
                    transition: 'background 150ms ease, color 150ms ease',
                    boxShadow: active ? '0 6px 16px rgba(14,37,55,0.18)' : 'none',
                    '&:hover': {
                        background: active
                            ? `linear-gradient(135deg, ${AppColors.primaryDark} 0%, #1d3a55 100%)`
                            : 'rgba(14,37,55,0.06)',
                    },
                }}
            >
                {item.icon}
            </Box>
            {badge > 0 && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        minWidth: 16,
                        height: 16,
                        px: 0.5,
                        borderRadius: '10px',
                        background: AppColors.errorRed,
                        color: '#fff',
                        fontSize: '0.6rem',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 4px rgba(244,67,54,0.4)',
                        animation: 'pulse 2s infinite',
                    }}
                >
                    {badge > 9 ? '9+' : badge}
                </Box>
            )}
        </Box>
    </Tooltip>
);

function defaultOpenGroups() {
    return NAV_SECTIONS.reduce((acc, s) => {
        acc[s.id] = true;
        return acc;
    }, {});
}

export default Sidebar;
